import { DateTime as DT } from 'luxon';
import type { WarrantyInput } from '@econtractor/types';

export interface WarrantyFormValues extends WarrantyInput {
    serialNumbers: string[];
}

export const DEFAULT_WARRANTY_MONTHS = 12;

export const todayIsoDate = (): string => DT.now().setZone('local').toISODate() || '';

export const addWarrantyMonths = (isoDate: string, months: number): string => {
    const start = DT.fromISO(isoDate, { zone: 'local' }).startOf('day');
    const count = Math.floor(months);
    if (!start.isValid || !Number.isInteger(count) || count < 1) return '';
    return start.plus({ months: count }).toISODate() || '';
};

/** Повні календарні місяці від дати придбання до дати закінчення. Не змінює дати. */
export const fullWarrantyMonths = (purchasedAt: string, warrantyUntil: string): number | null => {
    const start = DT.fromISO(purchasedAt, { zone: 'local' }).startOf('day');
    const end = DT.fromISO(warrantyUntil, { zone: 'local' }).startOf('day');
    if (!purchasedAt || !warrantyUntil || !start.isValid || !end.isValid) return null;
    if (end < start) return 0;

    let months = (end.year - start.year) * 12 + (end.month - start.month);
    if (!Number.isFinite(months) || months < 0) return 0;

    let guard = 0;
    while (months > 0 && start.plus({ months }).startOf('day') > end) {
        months -= 1;
        guard += 1;
        if (guard > 24) return 0;
    }
    return months;
};

/** Після повних місяців лишилися дні. Лише читання, дати не змінює. */
export const warrantyHasExtraDays = (purchasedAt: string, warrantyUntil: string): boolean => {
    const months = fullWarrantyMonths(purchasedAt, warrantyUntil);
    if (months == null) return false;
    const start = DT.fromISO(purchasedAt, { zone: 'local' }).startOf('day');
    const end = DT.fromISO(warrantyUntil, { zone: 'local' }).startOf('day');
    if (!start.isValid || !end.isValid || end <= start) return false;
    if (months < 1) return true;
    const exact = addWarrantyMonths(purchasedAt, months);
    return Boolean(exact) && warrantyUntil > exact;
};

export const emptyWarranty = (): WarrantyFormValues => {
    const purchasedAt = todayIsoDate();
    return {
        productId: 0,
        serialNumber: '',
        serialNumbers: [''],
        purchasedAt,
        warrantyUntil: addWarrantyMonths(purchasedAt, DEFAULT_WARRANTY_MONTHS),
        objectId: null,
        actualVendorId: null,
    };
};
